import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { useFlags, useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { useWindowWidth } from 'wsm-srp-utilities';
import { ErrorBoundary } from 'wsm-error-boundary';
import { useSelector, useDispatch } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { setOffSiteData } from '../actions/offSiteData';
import VehicleCard from './vehicleCard/VehicleCard';
import VehicleCardSkeleton from './vehicleCard/skeleton/VehicleCardSkeleton';
import ErrorAlert from './ErrorAlert';
import { getShouldShowCompare } from '../utilities/compare';
import { getFlagValue } from '../utilities/flags';
import { addImageParams } from '../utilities/images';
import { getIsSmallScreen } from '../utilities/layout';
import { waitForSomething } from '../utilities/waitForSomething';
import IntersectionWrapper from './IntersectionWrapper';
import createCTAandWIAPICards from '../utilities/createCTAandWIAPICards';

const VehicleCards = ({
	enableMediaCarousel,
	inventoryObj,
	isLoading,
	isMyCarsSitePropEnabled,
	isRequesting,
	isTransition,
	loadingDelayed
}) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const flags = useFlags();
	const { enableCompare } = usePrefs();
	const { deviceType } = useRequestData();

	// Layout
	const { layout } = useSelector((state) => state.layout);
	const viewportWidth = useWindowWidth();
	const isSmallScreen = getIsSmallScreen(viewportWidth);
	// "Grid View" only kicks in above tablet size.
	// A list-type horizontal card still shows for > 480 && < 768
	const forceGridView = flags['srp-hide-tabs-and-force-grid-view'];

	const isGridLayout = (layout === 'grid' && !isSmallScreen) || forceGridView;
	const isTabbedLayout = isSmallScreen || isGridLayout || forceGridView;
	const isTwoColumnGrid = viewportWidth < 1440 && isGridLayout;

	// Intersection Observer refs options and callback for intersection wrapper
	const observerRef = useRef(null);
	const observerCallback = useCallback((entries) => {
		const entry = entries[0];
		const { isIntersecting, target } = entry;
		const { uuid, location } = target.dataset;

		if (isIntersecting) {
			// Only dispatch the event if the WIAPI is available
			// and if the intersecting entry has a uuid (skeleton cards initially don't)
			if (isBrowser) {
				if (uuid || location) {
					const isLocation = location && !uuid;
					window.dispatchEvent(
						new CustomEvent('wsInvListingShown', {
							detail: {
								id: uuid || location,
								isLocation
							}
						})
					);
				}
			}
		}
	}, []);
	const srpDelayLoadDistance = getFlagValue(
		flags,
		'srp-delay-load-distance',
		'_toggleSRPDelayLoadDistance'
	);
	setNewRelicCustomAttribute('srpDelayLoadDistance', srpDelayLoadDistance);
	const observerOptions = {
		rootMargin: `${srpDelayLoadDistance}px`
	};

	// CTA + WIAPI cards
	const ctaCardItemlist = useSelector((state) => state.ctaCardItemlist);
	const CTAandWIAPICards = createCTAandWIAPICards(
		ctaCardItemlist,
		isGridLayout,
		observerOptions,
		observerRef,
		observerCallback
	);
	// index at which CTA and WIAPI cards are inserted into list of vehicles
	let myWalletIndex = 1; // list view case / smallscreen case
	if (isTwoColumnGrid) {
		myWalletIndex = 2; // grid view with 2 columns case
	} else if (isGridLayout) {
		myWalletIndex = 3; // grid view with 3 columns case
	}
	const CTA_WIAPI_CARD_POSITIONS = [myWalletIndex, 6, 10, 15];

	// Featured Promotion
	const featuredPromotionVIN =
		isBrowser && window?.DDC?.InvData?.urlParams?.featuredPromotion;
	const [featuredPromotionDom, setFeaturedPromotionDom] = useState(null);

	const srpUseImagePlaceholders = getFlagValue(
		flags,
		'srp-use-image-placeholders',
		'_toggleSRPUseImagePlaceholders'
	);
	setNewRelicCustomAttribute(
		'srpUseImagePlaceholders',
		typeof srpUseImagePlaceholders === 'boolean'
			? srpUseImagePlaceholders.toString()
			: null
	);

	const enableSSR =
		flags['ws-inv-data-preload-inventory'] && deviceType === 'MOBILE';

	const shouldShowCompare = getShouldShowCompare(
		viewportWidth,
		enableCompare,
		deviceType
	);

	const dealershipName =
		(isBrowser &&
			window.DDC &&
			window.DDC.dataLayer &&
			window.DDC.dataLayer.dealership &&
			window.DDC.dataLayer.dealership.dealershipName) ||
		'';

	// OFFSITE
	const [offSiteDataState, setOffSiteDataState] = useState(
		window?.DDC?.InvData?.getFacets().offSiteData || {
			offSiteBannerPosition: -1,
			offSiteItemCount: -1
		}
	);
	const { offSiteData } = window?.DDC?.InvData?.getFacets() || {};
	let enableOffSiteToggle = false;
	enableOffSiteToggle = offSiteData && offSiteData.offSiteItemCount > -1;
	useEffect(() => {
		if (enableOffSiteToggle) {
			setOffSiteDataState({ ...offSiteData });
			dispatch(setOffSiteData(offSiteData));
		}
	}, [offSiteData, enableOffSiteToggle, dispatch]);
	const [showOffSite, setShowOffSite] = useState(
		(sessionStorage.getItem('showOffSite') && true) || false
	);
	let offSiteBannerPosition;
	if (enableOffSiteToggle) {
		// eslint-disable-next-line prefer-destructuring
		offSiteBannerPosition =
			offSiteDataState.offSiteBannerPosition === -1
				? 0
				: offSiteDataState.offSiteBannerPosition;
	}

	const { incentives, accounts, inventory, pageInfo } = inventoryObj;

	const vehicleCardDefaultProps = {
		accounts,
		enableMediaCarousel,
		incentives,
		isGridLayout,
		isMyCarsSitePropEnabled,
		isTabbedLayout,
		observerRef,
		pageInfo,
		shouldShowCompare,
		loadingDelayed,
		usePlaceholderImage: srpUseImagePlaceholders
	};

	// wait for featured promotion vehicle
	// todo remove and use WIS
	useEffect(() => {
		if (!isLoading) {
			if (featuredPromotionVIN) {
				setFeaturedPromotionDom(
					<VehicleCardSkeleton
						isGridLayout={isGridLayout}
						isTabbedLayout={isTabbedLayout}
					/>
				);
			}
			waitForSomething({
				where: 'DDC.InvData.featuredPromotion',
				doBefore: () => {
					window.DDC.pubsub.subscribe(
						'ws-inv-data/vehicle-request-failed',
						() => {
							setFeaturedPromotionDom(null);
						}
					);
				},
				doAfter: () => {
					const tVehicle = window.DDC.InvData.featuredPromotion;
					if (featuredPromotionVIN) {
						setFeaturedPromotionDom(
							<IntersectionWrapper
								key={tVehicle.uuid}
								observerOptions={observerOptions}
								shouldFullyRender
								onIntersectCallback={observerCallback}
							>
								<VehicleCard
									{...vehicleCardDefaultProps}
									featuredPromotion
									vehicle={tVehicle}
									key={tVehicle.uuid}
								/>
							</IntersectionWrapper>
						);
					}
				},
				onGiveUp: () => {
					setFeaturedPromotionDom(null);
				}
			});
		}
	}, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

	// update featuredPromotionDom when layout changes
	// todo remove
	useEffect(() => {
		if (
			!isLoading &&
			featuredPromotionVIN &&
			window?.DDC?.InvData?.featuredPromotion
		) {
			const featuredPromotionVehicle =
				window.DDC.InvData.featuredPromotion;
			setFeaturedPromotionDom(
				<IntersectionWrapper
					key={featuredPromotionVehicle.uuid}
					observerOptions={observerOptions}
					shouldFullyRender
					onIntersectCallback={observerCallback}
				>
					<VehicleCard
						{...vehicleCardDefaultProps}
						featuredPromotion
						vehicle={featuredPromotionVehicle}
						key={featuredPromotionVehicle.uuid}
					/>
				</IntersectionWrapper>
			);
		}
	}, [isGridLayout]); // eslint-disable-line react-hooks/exhaustive-deps

	// update featuredPromotionDom when vin is cleared
	// todo remove
	useEffect(() => {
		if (!featuredPromotionVIN) {
			setFeaturedPromotionDom(null);
		}
	}, [featuredPromotionVIN]);

	const createVehicleCard = (vehicle, inventoryIndex) => {
		// Fully render cards above the fold
		const shouldThisVehicleFullyRender =
			(inventoryIndex === 0 && !isGridLayout) ||
			(inventoryIndex <= 2 && isGridLayout);

		const vehicleCardProps = {
			...vehicleCardDefaultProps,
			inventoryIndex,
			vehicle
		};

		const isFirstOffsiteCard = inventoryIndex === offSiteBannerPosition;

		if (
			enableOffSiteToggle &&
			vehicle.offSite &&
			!isFirstOffsiteCard &&
			!showOffSite
		) {
			return null;
		}

		const placeholderImageSrc =
			srpUseImagePlaceholders || shouldThisVehicleFullyRender
				? addImageParams(vehicle.images[0].uri)
				: null;

		return (
			<IntersectionWrapper
				key={vehicle.uuid}
				observerOptions={observerOptions}
				onIntersectCallback={observerCallback}
				shouldFullyRender={shouldThisVehicleFullyRender}
			>
				<VehicleCard
					{...vehicleCardProps}
					key={vehicle.uuid}
					placeholderImage={placeholderImageSrc}
					setShowOffSite={
						enableOffSiteToggle &&
						isFirstOffsiteCard &&
						!showOffSite
							? setShowOffSite
							: undefined
					}
					enableSSR={enableSSR}
				/>
			</IntersectionWrapper>
		);
	};

	let cards;

	if (isLoading) {
		cards = inventory.map((_i, index) => {
			// Apply different logic for 1st skeleton and SSR is enable
			if (index === 0 && enableSSR) {
				const haveFirstVehicleImg = enableSSR && _i?.images;
				// Check if the first vehicle image is available or not
				const useSSRPlaceholderImage =
					haveFirstVehicleImg && _i?.images[0]?.uri;
				return (
					<VehicleCardSkeleton
						key={_i.uuid}
						{...{ isGridLayout, isTabbedLayout }}
						// use default image as placeholder if there is no vehicle's 1st image
						enableFirstSkeletonImg={!haveFirstVehicleImg}
						ssrPlaceholderImage={
							useSSRPlaceholderImage
								? addImageParams(_i?.images[0]?.uri)
								: null
						}
					/>
				);
			}
			return (
				<VehicleCardSkeleton
					key={_i.uuid}
					{...{ isGridLayout, isTabbedLayout }}
				/>
			);
		});
	} else {
		if (!cards) {
			cards = [
				...inventory.flatMap((vehicle, vehicleIndex) => {
					const vehicleCard = createVehicleCard(
						vehicle,
						vehicleIndex
					);
					return CTA_WIAPI_CARD_POSITIONS.includes(vehicleIndex)
						? [CTAandWIAPICards.shift(), vehicleCard]
						: vehicleCard;
				}),
				...CTAandWIAPICards
			];
		}
		// Featured Promotion feature
		if (
			featuredPromotionVIN &&
			isBrowser &&
			window?.DDC?.InvData?.featuredPromotion &&
			cards
		) {
			// remove vehicle if already in list
			// todo remove
			cards = cards.filter(
				(card) =>
					card?.key !== window.DDC.InvData.featuredPromotion.uuid
			);
		}
	}

	return (
		<React.Fragment>
			{enableOffSiteToggle &&
				offSiteBannerPosition === 0 &&
				!showOffSite && (
					<ErrorAlert
						errorLabel={labels
							.get('CLEAR_MESSAGE_OFFSITE')
							.replace('{accountName}', dealershipName)}
					/>
				)}
			<ul
				className={setClassNames([
					isGridLayout ? 'vehicle-card-grid' : 'vehicle-card-list',
					'list-unstyled',
					'transition-property-opacity',
					'transition-duration-sm',
					'transition-timing-function-standard',
					isRequesting || isTransition ? 'opacity-25' : null
				])}
			>
				{featuredPromotionDom && (
					<ErrorBoundary newRelicPageAction="WS INV LISTING FEATURED PROMOTION ERROR BOUNDARY">
						{featuredPromotionDom}
					</ErrorBoundary>
				)}
				{cards}
			</ul>
		</React.Fragment>
	);
};

VehicleCards.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	isMyCarsSitePropEnabled: PropTypes.bool.isRequired,
	isRequesting: PropTypes.bool.isRequired,
	isTransition: PropTypes.bool.isRequired,
	inventoryObj: PropTypes.shape({
		incentives: PropTypes.shape({}),
		accounts: PropTypes.shape({}),
		pageInfo: PropTypes.shape({}),
		inventory: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	loadingDelayed: PropTypes.bool.isRequired
};

export default VehicleCards;
